import type { IconDefinition } from '@hg-akademie-ban/ban-elevate-assets';
import { faCircle } from '@hg-akademie-ban/ban-elevate-assets/icons/solid';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';
import { Icon } from '@/components/ui/icon.js';

type TagVariant =
  'accent' | 'accentSubtle' | 'brand' | 'brandSubtle' | 'brandKnockout' | 'neutral' | 'neutral 2' | 'outline';

type TagSize = 'default' | 'compact';

const tagBaseStyles = cn(['inline-flex', 'items-center', 'gap-4', 'rounded-xs', 'whitespace-nowrap']);

const tagVariantStyles: Record<TagVariant, string> = {
  accentSubtle: cn(['bg-accent-subtle', 'text-accent']),
  accent: cn(['bg-accent', 'text-primary-on-accent']),
  brandSubtle: cn(['bg-brand-subtle', 'text-brand']),
  brand: cn(['bg-brand', 'text-primary-knockout']),
  brandKnockout: cn(['bg-brand-knockout', 'text-primary-knockout']),
  neutral: cn(['bg-subtle', 'text-primary']),
  'neutral 2': cn(['bg-[#dbdbdb]', 'text-primary']), // TODO: use semantic color token once available
  outline: cn(['bg-default', 'text-primary', 'outline', 'outline-sm', 'outline-default']),
};

const tagSizeStyles: Record<TagSize, string> = {
  default: cn('px-8 py-4 label-default-light'),
  compact: cn('px-6 py-4 label-sm-light'),
};

const tagVariants = cva(tagBaseStyles, {
  variants: {
    variant: tagVariantStyles,
    size: tagSizeStyles,
  },
  defaultVariants: {
    variant: 'accent',
    size: 'default',
  },
});

export type TagProps = ComponentProps<'span'> &
  VariantProps<typeof tagVariants> & {
    children?: never;
    label: string;
    icon?: IconDefinition;
  };

/** A tag is a small UI element used to label, categorize, or highlight content. It helps provide contextual information and improves content organization. Tags should be visually distinct, consistently styled, and easy to scan for quick recognition.
 *
 * ```tsx
 * <Tag label="New" variant="accent" />
 * <Tag label="Featured" variant="brand" icon={faTag} />
 * <Tag label="Draft" variant="neutral" size="compact" />
 * ```
 */
export function Tag({ className, variant, size, label, icon, ...props }: TagProps) {
  return (
    <span data-slot="tag" className={cn(tagVariants({ variant, size, className }))} {...props}>
      {icon && <Icon icon={icon} size="small" />}
      {label}
    </span>
  );
}

const feedbackTagBaseStyles = tagBaseStyles; // Reuse base styles from Tag for consistency
const feedbackTagSizeStyles = tagSizeStyles; // Reuse size styles from Tag for consistency

const feedbackTagVariants = cva(feedbackTagBaseStyles, {
  variants: {
    feedback: {
      success: '',
      warning: '',
      error: '',
    },
    variant: {
      subtle: '',
      strong: '',
    },
    size: feedbackTagSizeStyles,
  },
  compoundVariants: [
    { feedback: 'success', variant: 'subtle', class: 'bg-feedback-success text-feedback-success' },
    {
      feedback: 'success',
      variant: 'strong',
      class: 'bg-feedback-success-strong text-feedback-success-strong',
    },
    { feedback: 'warning', variant: 'subtle', class: 'bg-feedback-warning text-feedback-warning' },
    {
      feedback: 'warning',
      variant: 'strong',
      class: 'bg-feedback-warning-strong text-feedback-warning-strong',
    },
    { feedback: 'error', variant: 'subtle', class: 'bg-feedback-error text-feedback-error' },
    { feedback: 'error', variant: 'strong', class: 'bg-feedback-error-strong text-feedback-error-strong' },
  ],
  defaultVariants: {
    feedback: 'success',
    variant: 'subtle',
    size: 'default',
  },
});

export type FeedbackTagProps = Omit<ComponentProps<'span'>, 'ref'> &
  VariantProps<typeof feedbackTagVariants> & {
    children?: never;
    label: string;
  };

/** FeedbackTag is a specialized variant for displaying status feedback with subtle and strong variants. It uses compound variants to combine `feedback` (success/warning/error) and `variant` (subtle/strong) props.
 *
 * ```tsx
 * <FeedbackTag label="Success" feedback="success" variant="strong" />
 * <FeedbackTag label="Warning" feedback="warning" variant="subtle" />
 * <FeedbackTag label="Error" feedback="error" variant="strong" size="compact" />
 * ```
 */
export function FeedbackTag({ className, feedback, variant, size, label, ...props }: FeedbackTagProps) {
  return (
    <span
      data-slot="feedback-tag"
      className={cn(feedbackTagVariants({ feedback, variant, size, className }))}
      {...props}
    >
      {label}
    </span>
  );
}

type StatusTagVariant = 'green' | 'yellow' | 'red' | 'grey' | 'brand';

const statusTagBaseStyles = cn(tagBaseStyles, 'px-8 py-4 label-default-light bg-default');

const statusTagVariantStyles: Record<StatusTagVariant, string> = {
  green: cn('text-feedback-success'),
  yellow: cn('text-feedback-warning'),
  red: cn('text-feedback-error'),
  grey: cn('text-tertiary'),
  brand: cn('text-brand'),
};

const statusTagVariants = cva(statusTagBaseStyles, {
  variants: {
    variant: statusTagVariantStyles,
  },
  defaultVariants: {
    variant: 'brand',
  },
});

export type StatusTagProps = ComponentProps<'span'> &
  VariantProps<typeof statusTagVariants> & {
    children?: never;
    label: string;
  };

/** A status tag displays colored status indicators with a leading ellipsis icon. Used for showing states like active, pending, or error conditions.
 *
 * ```tsx
 * <StatusTag label="Active" variant="green" />
 * <StatusTag label="Pending" variant="yellow" />
 * <StatusTag label="Error" variant="red" />
 * ```
 */
export function StatusTag({ className, variant, label, ...props }: StatusTagProps) {
  return (
    <span data-slot="status-tag" className={cn(statusTagVariants({ variant, className }))} {...props}>
      <Icon icon={faCircle} className="size-10" />
      {label}
    </span>
  );
}

export type SubtleTagProps = ComponentProps<'span'> & {
  children?: never;
  label: string;
  icon: IconDefinition;
};

/** A minimal, background-free tag that always renders at compact size with a required icon. Both icon and label render in `text-tertiary`. Use when a subtle, low-emphasis label is needed without a filled background.
 *
 * ```tsx
 * <SubtleTag label="Optional" icon={faTag} />
 * <SubtleTag label="Draft" icon={faPencil} />
 * ```
 */
export function SubtleTag({ className, label, icon, ...props }: SubtleTagProps) {
  return (
    <span
      data-slot="tag-subtle"
      className={cn(
        'label-default-light text-tertiary inline-flex items-center gap-8 py-4 whitespace-nowrap',
        className,
      )}
      {...props}
    >
      <Icon icon={icon} size="small" />
      {label}
    </span>
  );
}
